/**
 * @file: AuthenticationInvalidationMiddleware.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */
import { sessionExpired$ } from '@euler/functions/sessionTracking';
import { ResponseContext } from '@euler/lib/request';
import { Middleware } from '@euler/lib/request/Middleware';

/**
 * The refresh outcomes that mean the SESSION is gone, as opposed to this one
 * attempt having failed. Everything else is deliberately excluded:
 *
 * - `undefined` — the request carried no `Authorization` header, so its 401 says
 *   nothing about our session and must never end it.
 * - `not-attempted` — reading the stored token threw before we could try.
 * - `refresh-failed` — transport or server-side failure; the token may well
 *   still be good.
 * - `succeeded` — the retry itself came back 401, which is an authorization
 *   problem with that endpoint, not an expired session.
 */
const kSessionEndingRefreshStatuses = new Set<
  ResponseContext['authRefreshStatus']
>(['missing-refresh-token', 'expired-refresh-token', 'refresh-token-rejected']);

export class AuthenticationInvalidationMiddleware implements Middleware {
  async post(context: ResponseContext) {
    if (context.response.status !== 401) return;
    if (!kSessionEndingRefreshStatuses.has(context.authRefreshStatus)) return;
    sessionExpired$.next();
  }
}
